import { request } from './client.js';

export type AdminBookAssetKind = 'cover_image' | 'scene_image' | 'scene_video' | 'narration_audio';
export type AdminBookLayout = 'split' | 'full-bleed' | 'band';
export type AdminBookTextPosition =
  'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface AdminBookBuilderOptions {
  readonly users: readonly {
    readonly id: string;
    readonly name: string;
    readonly email: string;
    readonly role: 'user' | 'admin';
  }[];
  readonly styles: readonly {
    readonly id: string;
    readonly name: string;
    readonly isSystem: boolean;
  }[];
}

export interface AdminBookAsset {
  readonly id: string;
  readonly kind: AdminBookAssetKind;
  readonly mime: string;
  readonly bytes: number;
  readonly width: number | null;
  readonly height: number | null;
  readonly durationMs: number | null;
  readonly url: string;
  readonly urlExpiresAt: string;
}

export interface PublishAdminBookInput {
  readonly ownerId: string;
  readonly styleId: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly dedication: string | null;
  readonly defaultLayout: AdminBookLayout;
  readonly coverAssetId: string;
  readonly backCoverAssetId: string;
  readonly spreads: readonly {
    readonly id?: string | null;
    readonly layoutId: AdminBookLayout | null;
    readonly textBlocks: readonly {
      readonly textRu: string;
      readonly position: AdminBookTextPosition;
    }[];
    readonly bandImageRatio: number | null;
    readonly imageAssetId: string;
    readonly videoAssetId: string | null;
    readonly narrationAssetId: string | null;
  }[];
}

export interface PublishedAdminBook {
  readonly bookId: string;
  readonly publicUrl: string;
  readonly publishedAt: string;
  readonly spreadCount: number;
}

export interface EditableAdminBook {
  readonly bookId: string;
  readonly ownerId: string;
  readonly styleId: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly dedication: string | null;
  readonly defaultLayout: AdminBookLayout;
  readonly cover: AdminBookAsset | null;
  readonly backCover: AdminBookAsset | null;
  readonly spreads: readonly {
    readonly id: string;
    readonly layoutId: AdminBookLayout | null;
    readonly textBlocks: readonly {
      readonly textRu: string;
      readonly position: AdminBookTextPosition;
    }[];
    readonly bandImageRatio: number | null;
    readonly image: AdminBookAsset | null;
    readonly video: AdminBookAsset | null;
    readonly narration: AdminBookAsset | null;
  }[];
}

export interface SavedAdminBook {
  readonly bookId: string;
  readonly updatedAt: string;
  readonly spreadCount: number;
}

interface PresignResponse {
  readonly storageKey: string;
  readonly uploadUrl: string;
  readonly headers: {
    readonly 'content-type': string;
    readonly 'cache-control'?: string;
  };
}

export const adminBookBuilderApi = {
  options: (ownerId?: string) =>
    request<AdminBookBuilderOptions>(
      `/admin/book-builder/options${ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : ''}`,
    ),

  async upload(ownerId: string, kind: AdminBookAssetKind, file: File): Promise<AdminBookAsset> {
    const mime = normalizedMime(file, kind);
    const presigned = await request<PresignResponse>('/admin/book-builder/assets/presign', {
      method: 'POST',
      body: { ownerId, filename: file.name, mime, bytes: file.size, kind },
    });
    const uploaded = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: presigned.headers,
      body: file,
    });
    if (!uploaded.ok) throw new Error(`upload failed: ${uploaded.status}`);
    return request<AdminBookAsset>('/admin/book-builder/assets/confirm', {
      method: 'POST',
      body: { ownerId, storageKey: presigned.storageKey, kind },
    });
  },

  publish: (input: PublishAdminBookInput) =>
    request<PublishedAdminBook>('/admin/book-builder/publish', {
      method: 'POST',
      body: input,
    }),
  editableBook: (bookId: string) =>
    request<EditableAdminBook>(`/admin/book-builder/books/${bookId}`),
  save: (bookId: string, input: PublishAdminBookInput) =>
    request<SavedAdminBook>(`/admin/book-builder/books/${bookId}`, {
      method: 'PATCH',
      body: input,
    }),
};

function normalizedMime(file: File, kind: AdminBookAssetKind): string {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (kind === 'scene_video') {
    return extension === 'webm' ? 'video/webm' : 'video/mp4';
  }
  if (kind === 'narration_audio') {
    const byExtension: Readonly<Record<string, string>> = {
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      m4a: 'audio/mp4',
      mp4: 'audio/mp4',
      ogg: 'audio/ogg',
      oga: 'audio/ogg',
      opus: 'audio/ogg',
      webm: 'audio/webm',
    };
    return (extension && byExtension[extension]) || file.type;
  }
  const byExtension: Readonly<Record<string, string>> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
  };
  return (extension && byExtension[extension]) || file.type;
}
